import { gql } from "@apollo/client";

import { CitationFragment } from "@apps/ask-ai/graphql/get-ask-ai-session-query";
import CommonGoalArtifactFragment from "@graphql/common-goal-artifact-fragment";

import coreValueAssessmentQuestionFragment from "./core-value-assessment-question-fragment";

export default gql`
  ${CitationFragment}
  ${CommonGoalArtifactFragment}
  ${coreValueAssessmentQuestionFragment}
  fragment AssessmentDeliveryAssessmentGroupSection on AssessmentSectionInterface {
    id
    title
    description
    subjectVisibility
    questions {
      edges {
        node {
          id
          question {
            ... on RangeAssessmentQuestionNode {
              id
              title
              description
              startValue
              endValue
              labels
              labelDescriptions
              comment
              isResponseMandatory
              responses
              questionType
              responseVisibility
            }
            ... on IndividualGoalAssessmentQuestionNode {
              id
              title
              description
              startValue
              endValue
              labels
              labelDescriptions
              comment
              isResponseMandatory
              responses
              questionType
              responseVisibility
              goal {
                ...CommonGoalArtifactFragment
              }
            }
            ... on OverallGoalAssessmentQuestionNode {
              id
              title
              description
              startValue
              endValue
              labels
              labelDescriptions
              comment
              isResponseMandatory
              responses
              questionType
              responseVisibility
            }
            ... on IndividualRecognitionCoreValueAssessmentQuestionNode {
              id
              title
              description
              startValue
              endValue
              labels
              labelDescriptions
              comment
              isResponseMandatory
              responses
              questionType
              responseVisibility
              coreValue {
                ...AssessmentQuestionCoreValue
              }
            }
            ... on OverallRecognitionCoreValueAssessmentQuestionNode {
              id
              title
              description
              startValue
              endValue
              labels
              labelDescriptions
              comment
              isResponseMandatory
              responses
              questionType
              responseVisibility
            }
            ... on CompetencyAssessmentQuestionNode {
              id
              title
              description
              startValue
              endValue
              labels
              labelDescriptions
              comment
              isResponseMandatory
              responses
              questionType
              responseVisibility
              role {
                id
                title
                level
              }
              competency {
                id
                title
                criteria {
                  edges {
                    node {
                      id
                      content
                    }
                  }
                }
              }
            }
            ... on CompetencyCriteriaAssessmentQuestionNode {
              id
              title
              description
              startValue
              endValue
              labels
              labelDescriptions
              comment
              isResponseMandatory
              responses
              questionType
              responseVisibility
              role {
                id
                title
                level
              }
              competencyCriteria {
                id
                content
                competency {
                  id
                  title
                  description
                }
              }
            }
            ... on ResponsibilityAssessmentQuestionNode {
              id
              title
              description
              startValue
              endValue
              labels
              labelDescriptions
              comment
              isResponseMandatory
              responses
              questionType
              responseVisibility
              responsibility {
                id
                content
                role {
                  id
                  title
                  level
                }
              }
            }
            ... on TalentIndicatorAssessmentQuestionNode {
              id
              title
              description
              startValue
              endValue
              labels
              labelDescriptions
              comment
              isResponseMandatory
              responses
              questionType
              responseVisibility
              talentIndicator {
                id
                talentIndicatorType
              }
            }
            ... on MultiChoiceAssessmentQuestionNode {
              id
              title
              description
              options
              optionDescriptions
              comment
              isResponseMandatory
              responses
              questionType
              responseVisibility
            }
            ... on TextAssessmentQuestionNode {
              id
              title
              description
              comment
              isResponseMandatory
              responses
              questionType
              responseVisibility
            }
            ... on NpsAssessmentQuestionNode {
              id
              title
              description
              comment
              isResponseMandatory
              responses
              questionType
              responseVisibility
            }
          }
        }
      }
    }
  }

  fragment AssessmentDeliveryTemplate on AssessmentTemplateNode {
    id
    assessmentType
    anonymity
    delivery
    questionSets {
      edges {
        node {
          id
          title
          sections {
            edges {
              node {
                ...AssessmentDeliveryAssessmentGroupSection
              }
            }
          }
        }
      }
    }
  }

  fragment AssessmentDelivery on AssessmentDeliveryNode {
    id
    state
    summary
    calibrationAssessmentId
    excludedAnswerIds
    deliveryDatetime
    dueDate
    periodStartDate
    periodEndDate
    targetCurrentRoleIds
    canUpdate {
      permission
      reason
    }
    creator {
      id
      name
    }
    target {
      id
      name
      email
      firstName
      avatar
      organizationSpecificInfo(organizationId: $organizationId) {
        jobLevel
        startDate
      }
      managers {
        edges {
          node {
            id
            name
            email
            firstName
            avatar
          }
        }
      }
    }
    targetPosition
    relatedMeetings {
      edges {
        node {
          id
          title
          meetingGroupId
          startDatetime
          endDatetime
          draft
          isFinalized
          canUpdate {
            permission
          }
        }
      }
    }
    mostRecentAnswers {
      questionId
      questionType
      integerAnswer
      submitted
    }
    complianceProgram {
      id
      title
      periodStartDate
      periodEndDate
      calibration
      canReadDetails {
        permission
        reason
      }
      performanceAssessmentTemplate {
        ...AssessmentDeliveryTemplate
      }
      managerAssessmentTemplate {
        ...AssessmentDeliveryTemplate
      }
      peerAssessmentTemplate {
        ...AssessmentDeliveryTemplate
      }
      engagementSurveyTemplate {
        ...AssessmentDeliveryTemplate
      }
      requiredTopicTemplates(first: 1) {
        edges {
          node {
            id
          }
        }
      }
      participantStatus {
        edges {
          node {
            performanceAssessmentStatus
            performanceSelfAssessmentStatus
            managerAssessmentStatus
            managerSelfAssessmentStatus
            peerAssessmentStatus
            peerSelfAssessmentStatus
            user {
              id
              name
              email
              avatar
            }
          }
        }
      }
    }
    additionalQuestionSets {
      performance {
        edges {
          node {
            id
            title
            sections {
              edges {
                node {
                  ...AssessmentDeliveryAssessmentGroupSection
                }
              }
            }
          }
        }
      }
      peer {
        edges {
          node {
            id
            title
            sections {
              edges {
                node {
                  ...AssessmentDeliveryAssessmentGroupSection
                }
              }
            }
          }
        }
      }
      manager {
        edges {
          node {
            id
            title
            sections {
              edges {
                node {
                  ...AssessmentDeliveryAssessmentGroupSection
                }
              }
            }
          }
        }
      }
    }
    answers {
      edges {
        node {
          ... on RangeAssessmentAnswerNode {
            id
            responder {
              id
              name
              email
              firstName
              avatar
            }
            questionId
            integerAnswer
            comment
          }
          ... on IndividualGoalAssessmentAnswerNode {
            id
            responder {
              id
              name
              email
              firstName
              avatar
            }
            questionId
            integerAnswer
            comment
          }
          ... on OverallGoalAssessmentAnswerNode {
            id
            responder {
              id
              name
              email
              firstName
              avatar
            }
            questionId
            integerAnswer
            comment
          }
          ... on IndividualRecognitionCoreValueAssessmentAnswerNode {
            id
            responder {
              id
              name
              email
              firstName
              avatar
            }
            questionId
            integerAnswer
            comment
          }
          ... on OverallRecognitionCoreValueAssessmentAnswerNode {
            id
            responder {
              id
              name
              email
              firstName
              avatar
            }
            questionId
            integerAnswer
            comment
          }
          ... on ResponsibilityAssessmentAnswerNode {
            id
            responder {
              id
              name
              email
              firstName
              avatar
            }
            questionId
            integerAnswer
            comment
          }
          ... on TalentIndicatorAssessmentAnswerNode {
            id
            responder {
              id
              name
              email
              firstName
              avatar
            }
            questionId
            integerAnswer
            comment
          }
          ... on CompetencyAssessmentAnswerNode {
            id
            responder {
              id
              name
              email
              firstName
              avatar
            }
            questionId
            integerAnswer
            comment
          }
          ... on CompetencyCriteriaAssessmentAnswerNode {
            id
            responder {
              id
              name
              email
              firstName
              avatar
            }
            questionId
            integerAnswer
            comment
          }
          ... on MultiChoiceAssessmentAnswerNode {
            id
            responder {
              id
              name
              email
              firstName
              avatar
            }
            questionId
            choices
            comment
          }
          ... on TextAssessmentAnswerNode {
            id
            responder {
              id
              name
              email
              firstName
              avatar
            }
            questionId
            textAnswer
            comment
            inferredSources {
              edges {
                node {
                  ...CitationFragment
                }
              }
            }
          }
          ... on NpsAssessmentAnswerNode {
            id
            responder {
              id
              name
              email
              firstName
              avatar
            }
            questionId
            integerAnswer
            comment
          }
        }
      }
    }
  }

  query getAssessmentDelivery(
    $assessmentDeliveryId: IntOrString!
    $organizationId: Int!
  ) {
    assessmentDelivery(id: $assessmentDeliveryId) {
      ...AssessmentDelivery
      undeliveredPeerAssessmentDeliveryId
    }
  }
`;
